import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, AlertCircle, Activity } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import api from "../../lib/apiClient";
import { cacheInvalidate } from "../../lib/cache";
import ChannelCreatePanel from "./channels/ChannelCreatePanel";
import ChannelListPanel from "./channels/ChannelListPanel";
import ChannelPausePanel from "./channels/ChannelPausePanel";
import ChannelRow from "./channels/ChannelRow";

/**
 * ChannelsTab — owner-level channel management on /dashboard.
 *
 * Lists every channel the user owns and exposes:
 *   - Create  (Producer-only)
 *   - Act as  (switches the active channel)
 *   - Go dark / Resume  (pause / unpause)
 *   - List for sale / Cancel listing (marketplace)
 *
 * The visual composition lives in 4 sibling components under
 * `./channels/`. This file owns data fetching, the orchestrated panel
 * states, and the action handlers — keeping React state colocated with
 * the network calls that mutate it.
 */
const ChannelsTab = ({ user, refreshUser }) => {
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [config, setConfig] = useState({
    creation_cost: 100, quarterly_maint_cost: 150,
    go_dark_week_options: [1, 2, 4], go_dark_cost_per_week: 25,
  });
  const [loading, setLoading] = useState(true);

  // Per-action UI state. Each panel is a separate mode so opening one
  // implicitly closes the others (parent passes a single target prop
  // to its child panels — null means hidden).
  const [createOpen, setCreateOpen] = useState(false);
  const [newHandle, setNewHandle] = useState("");
  const [creating, setCreating] = useState(false);

  const [pauseTarget, setPauseTarget] = useState(null);
  const [pauseWeeks, setPauseWeeks] = useState(1);
  const [pauseAutoRenew, setPauseAutoRenew] = useState(true);
  const [pausing, setPausing] = useState(false);

  const [listTarget, setListTarget] = useState(null);
  const [listPrice, setListPrice] = useState(500);
  const [listing, setListing] = useState(false);

  // Owner's existing listings keyed by channel_id — drives the
  // "Listed for X cr" badge + "Cancel listing" inline action.
  const [myListings, setMyListings] = useState({});

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/me/channels`);
      setChannels(data.items || []);
      setActiveId(data.active_channel_id);
      setConfig({
        creation_cost: data.creation_cost,
        quarterly_maint_cost: data.quarterly_maint_cost,
        go_dark_week_options: data.go_dark_week_options,
        go_dark_cost_per_week: data.go_dark_cost_per_week,
      });
      try {
        const { data: ml } = await api.get(`/me/marketplace/listings`);
        const map = {};
        (ml.items || []).filter((l) => l.status === "active")
          .forEach((l) => { map[l.channel_id] = l; });
        setMyListings(map);
      } catch {
        // Marketplace listings are non-critical — failure here just
        // hides the "Listed" badge. Channels list itself is unaffected.
      }
    } catch {
      toast.error("Failed to load channels");
    }
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const tierKey = (user.subscription_tier || "free").toLowerCase();
  const isProducer = tierKey === "producer";

  // --- Action handlers (kept here because they mutate the local state
  // the panels and rows render against — pulling them into a hook would
  // require lifting EVERY piece of state, with no real reuse benefit).
  const handleCreate = async () => {
    const handle = newHandle.trim().toLowerCase();
    if (!handle) return;
    setCreating(true);
    try {
      await api.post(`/channels`, { username: handle });
      toast.success(`/${handle} created`);
      setNewHandle("");
      setCreateOpen(false);
      await reload();
      await refreshUser?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create channel");
    }
    setCreating(false);
  };

  const handleActivate = async (ch) => {
    if (ch.id === activeId) return;
    if (ch.is_paused) {
      toast.error("Resume the channel first.");
      return;
    }
    try {
      await api.post(`/me/channels/${ch.id}/activate`, {});
      toast.success(`Now acting as /${ch.username}`);
      setActiveId(ch.id);
      await refreshUser?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't switch.");
    }
  };

  const handlePause = async () => {
    if (!pauseTarget) return;
    setPausing(true);
    try {
      await api.post(`/me/channels/${pauseTarget.id}/pause`, { weeks: pauseWeeks, auto_renew: pauseAutoRenew });
      // Public profile renders `is_paused` — bust the cache so the
      // "Dark" badge shows up on /:channelUsername without a hard reload.
      if (pauseTarget.username) {
        cacheInvalidate(`profile:${pauseTarget.username.toLowerCase()}`);
      }
      toast.success(`/${pauseTarget.username} is going dark.`);
      setPauseTarget(null);
      await reload();
      await refreshUser?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to pause.");
    }
    setPausing(false);
  };

  const handleResume = async (ch) => {
    if (!window.confirm(`Resume /${ch.username}? Remaining dark-week credits won't be refunded.`)) return;
    try {
      await api.post(`/me/channels/${ch.id}/resume`, {});
      if (ch.username) cacheInvalidate(`profile:${ch.username.toLowerCase()}`);
      toast.success(`/${ch.username} is live again.`);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resume.");
    }
  };

  const handleList = async () => {
    if (!listTarget) return;
    const price = parseInt(listPrice, 10);
    if (Number.isNaN(price) || price < 100) {
      toast.error("Listing price must be at least 100 credits.");
      return;
    }
    setListing(true);
    try {
      await api.post(`/marketplace/listings`, { channel_id: listTarget.id, price_credits: price });
      toast.success(`/${listTarget.username} is now on the market for ${price} credits.`);
      setListTarget(null);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to list channel.");
    }
    setListing(false);
  };

  const handleCancelListing = async (ch) => {
    const lst = myListings[ch.id];
    if (!lst) return;
    if (!window.confirm(`Cancel the listing for /${ch.username}?`)) return;
    try {
      await api.delete(`/marketplace/listings/${lst.id}`);
      toast.success(`/${ch.username} de-listed.`);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <Card className="bg-gray-900 border-gray-800" data-testid="channels-tab">
      <CardHeader className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-emerald-400" /> Your Channels
            </CardTitle>
            <CardDescription className="text-gray-400 text-xs sm:text-sm mt-1">
              {isProducer
                ? `Unlimited channels · ${config.creation_cost} cr to launch · ${config.quarterly_maint_cost} cr/quarter to maintain`
                : "Subscribe to Producer to launch additional anonymous channels"}
            </CardDescription>
          </div>
          {isProducer && !createOpen && (
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-sm whitespace-nowrap"
              data-testid="new-channel-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> New channel
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0">
        {createOpen && isProducer && (
          <ChannelCreatePanel
            creationCost={config.creation_cost}
            handle={newHandle}
            onHandle={setNewHandle}
            busy={creating}
            onSubmit={handleCreate}
            onCancel={() => { setCreateOpen(false); setNewHandle(""); }}
          />
        )}
        <ChannelListPanel
          target={listTarget}
          price={listPrice}
          onPrice={setListPrice}
          busy={listing}
          onSubmit={handleList}
          onCancel={() => setListTarget(null)}
        />
        <ChannelPausePanel
          target={pauseTarget}
          weekOptions={config.go_dark_week_options}
          costPerWeek={config.go_dark_cost_per_week}
          weeks={pauseWeeks}
          onWeeks={setPauseWeeks}
          autoRenew={pauseAutoRenew}
          onAutoRenew={setPauseAutoRenew}
          busy={pausing}
          onSubmit={handlePause}
          onCancel={() => setPauseTarget(null)}
        />
        <div className="space-y-2">
          {channels.map((ch) => (
            <ChannelRow
              key={ch.id}
              ch={ch}
              activeId={activeId}
              listing={myListings[ch.id]}
              goDarkCostPerWeek={config.go_dark_cost_per_week}
              onActivate={() => handleActivate(ch)}
              onResume={() => handleResume(ch)}
              onGoDark={() => { setPauseTarget({ id: ch.id, username: ch.username }); setPauseWeeks(1); }}
              onList={() => { setListTarget({ id: ch.id, username: ch.username }); setListPrice(500); }}
              onCancelListing={() => handleCancelListing(ch)}
            />
          ))}
        </div>
        {!isProducer && (
          <div className="mt-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/30 text-xs text-amber-100 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-400" />
            <div>
              <strong>Producer-tier perk:</strong> launch unlimited anonymous channels.
              Open new niches under handles that aren't traceable back to your master account.
              {" "}
              <a href="/pricing" className="underline text-amber-300">Upgrade →</a>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ChannelsTab;
